import React, { useEffect, useState } from 'react';
import { Film, Music } from 'lucide-react';
import { mediaApi, displayStatusFromMediaAsset } from '@/api/mediaWrapper';
import Image from '@/icons/Image';
import { getMediaFileCategoryFromMimeType } from '@/utils/mediaUpload';

import './story-media-thumb.css';

interface StoryMediaThumbProps {
  familyId: string;
  mediaAssetId: string;
  mimeType?: string;
  size?: '40x40' | '200x200';
  className?: string;
  onClick?: () => void;
  label?: string;
  /**
   * When true, processing media can still be clicked (e.g. gallery picker to attach).
   * Viewers should leave this false so processing/unavailable items stay inert.
   */
  allowSelectWhileProcessing?: boolean;
}

const StoryMediaThumb: React.FC<StoryMediaThumbProps> = ({
  familyId,
  mediaAssetId,
  mimeType: mimeTypeProp,
  size = '40x40',
  className = '',
  onClick,
  label,
  allowSelectWhileProcessing = false
}) => {
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [displayStatus, setDisplayStatus] = useState<'safe' | 'processing' | 'unavailable' | null>(null);
  const [statusDetail, setStatusDetail] = useState<string | null>(null);
  const [resolvedMimeType, setResolvedMimeType] = useState<string | undefined>(mimeTypeProp);
  const mimeType = mimeTypeProp ?? resolvedMimeType;
  const category = getMediaFileCategoryFromMimeType(mimeType);
  const isVideo = category === 'video';
  const isAudio = category === 'audio';
  const mimeKnown = Boolean(mimeType);

  useEffect(() => {
    setResolvedMimeType(mimeTypeProp);
  }, [mimeTypeProp]);

  useEffect(() => {
    let cancelled = false;
    if (mimeTypeProp && !isVideo) return;
    mediaApi.getMediaAsset(familyId, mediaAssetId).then((result) => {
      if (cancelled || !result.success || !result.data) return;
      if (!mimeTypeProp && result.data.mimeType) {
        setResolvedMimeType(result.data.mimeType);
      }
      if (getMediaFileCategoryFromMimeType(result.data.mimeType) === 'video') {
        const derived = displayStatusFromMediaAsset(result.data);
        setDisplayStatus(derived.displayStatus);
        setStatusDetail(derived.message ?? null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [familyId, mediaAssetId, mimeTypeProp, isVideo]);

  useEffect(() => {
    let cancelled = false;
    if (!mimeKnown || isVideo || isAudio) return;
    mediaApi.getMediaDisplay(familyId, mediaAssetId, size).then((result) => {
      if (cancelled) return;
      if (result.success && result.data) {
        setDisplayUrl(result.data.url);
        setDisplayStatus(result.data.displayStatus);
        setStatusDetail(result.data.message ?? null);
      } else {
        setDisplayStatus('unavailable');
        setStatusDetail(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [familyId, mediaAssetId, size, mimeKnown, isVideo, isAudio]);

  useEffect(() => {
    if (!isAudio) return;
    setDisplayUrl(null);
    setDisplayStatus('safe');
    setStatusDetail(null);
  }, [isAudio]);

  useEffect(() => {
    let cancelled = false;
    if (!isVideo) return;
    if (displayStatus == null) {
      setDisplayUrl(null);
      return;
    }
    if (displayStatus !== 'safe') {
      setDisplayUrl(null);
      return;
    }
    mediaApi.getVideoPosterUrl(familyId, mediaAssetId).then((url) => {
      if (cancelled) return;
      setDisplayUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [familyId, mediaAssetId, isVideo, displayStatus]);

  const showImage = displayStatus === 'safe' && Boolean(displayUrl) && !isAudio;
  const iconPx = size === '200x200' ? 40 : 22;
  const isProcessing = displayStatus === 'processing' || (isVideo && displayStatus === null);
  const isUnavailable = displayStatus === 'unavailable';
  const compact = size === '40x40';
  const statusLabel = isProcessing
    ? statusDetail || (isVideo ? 'Processing video…' : 'Processing…')
    : isUnavailable
      ? statusDetail || (isVideo ? 'Video unavailable' : 'Unavailable')
      : null;
  /** Compact thumbs: keep icon inside the square; put the readable message outside. */
  const externalCaption = compact && statusLabel ? statusLabel : null;
  const internalStatusLabel = !compact && statusLabel ? statusLabel : null;

  const openTitle =
    label ??
    (isProcessing && isVideo
      ? 'Processing video…'
      : isUnavailable && isVideo
        ? 'Video unavailable'
        : isVideo
          ? 'Play video'
          : isAudio
            ? 'Play audio'
            : 'View attachment');

  const content = showImage ? (
    <img src={displayUrl!} alt="" className="story-media-thumb__img" loading="lazy" />
  ) : (
    <span
      className={`story-media-thumb__placeholder${
        internalStatusLabel ? ' story-media-thumb__placeholder--status' : ''
      }`}
      aria-hidden
    >
      {internalStatusLabel ? (
        <>
          {isVideo ? (
            <Film width={iconPx} height={iconPx} strokeWidth={1.75} />
          ) : isAudio ? (
            <Music width={iconPx} height={iconPx} strokeWidth={1.75} />
          ) : null}
          <span className="story-media-thumb__status">{internalStatusLabel}</span>
        </>
      ) : !mimeKnown && !statusLabel ? (
        '…'
      ) : isVideo || isProcessing || (isUnavailable && isVideo) ? (
        <Film width={iconPx} height={iconPx} strokeWidth={1.75} />
      ) : isAudio ? (
        <Music width={iconPx} height={iconPx} strokeWidth={1.75} />
      ) : (
        <Image width={iconPx} height={iconPx} />
      )}
    </span>
  );

  const classes = `story-media-thumb story-media-thumb--${size} ${className}`.trim();
  const canClick =
    Boolean(onClick) && (displayStatus === 'safe' || (allowSelectWhileProcessing && displayStatus === 'processing'));

  const thumb = onClick ? (
    <button
      type="button"
      className={`${classes} story-media-thumb--button`}
      onClick={canClick ? onClick : undefined}
      disabled={!canClick}
      title={openTitle}
      aria-label={openTitle}
    >
      {content}
    </button>
  ) : (
    <div className={classes} title={statusLabel || undefined}>
      {content}
    </div>
  );

  if (externalCaption) {
    return (
      <div className="story-media-thumb-wrap">
        {thumb}
        <span className="story-media-thumb__caption">{externalCaption}</span>
      </div>
    );
  }

  return thumb;
};

export default StoryMediaThumb;
